import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_OPTIONS = [
  { value: "compact", label: "Compact Racing Activation" },
  { value: "professional", label: "Professional Racing Activation" },
  { value: "premium", label: "Premium Trade Show Activation" },
  { value: "motion", label: "3DOF Motion Trade Show Activation" },
  { value: "unsure", label: "Unsure / Custom" },
];

const EMPTY = {
  name: "",
  company: "",
  email: "",
  phone: "",
  event_name: "",
  event_dates: "",
  details: "",
};

const Field = ({ label, htmlFor, children, full = false }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label
      htmlFor={htmlFor}
      className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 mb-2"
    >
      {label}
    </label>
    {children}
  </div>
);

const inputCls =
  "bg-ink/60 border-line text-slate-100 placeholder:text-slate-600 focus-visible:ring-electric h-11 rounded-none";

export const QuoteModal = ({ open, onOpenChange, tier, onTierChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, tier });
      toast.success("Quote request received. Our team will reach out shortly.");
      setForm(EMPTY);
      onOpenChange(false);
    } catch {
      toast.error("Something went wrong sending your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-panel border-line text-slate-100 max-w-2xl w-[calc(100vw-2rem)] max-h-[88vh] overflow-y-auto rounded-none clip-corner p-6 sm:p-8"
        data-testid="quote-request-modal"
      >
        <DialogHeader>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-electric mb-2">
            Event Pricing
          </p>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-slate-50">
            Request a Quote
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Tell us about your event and our team will build a tailored simulator package for you.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5 mt-4" data-testid="quote-form">
          <Field label="Name *" htmlFor="quote-name">
            <Input
              id="quote-name"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Jordan Smith"
              className={inputCls}
              data-testid="quote-input-name"
            />
          </Field>
          <Field label="Company Name" htmlFor="quote-company">
            <Input
              id="quote-company"
              value={form.company}
              onChange={set("company")}
              placeholder="Acme Corp"
              className={inputCls}
              data-testid="quote-input-company"
            />
          </Field>
          <Field label="Email *" htmlFor="quote-email">
            <Input
              id="quote-email"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              placeholder="you@company.com"
              className={inputCls}
              data-testid="quote-input-email"
            />
          </Field>
          <Field label="Phone Number" htmlFor="quote-phone">
            <Input
              id="quote-phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder="(555) 123-4567"
              className={inputCls}
              data-testid="quote-input-phone"
            />
          </Field>
          <Field label="Event Name / Location" htmlFor="quote-event">
            <Input
              id="quote-event"
              value={form.event_name}
              onChange={set("event_name")}
              placeholder="SEMA Show — Las Vegas"
              className={inputCls}
              data-testid="quote-input-event"
            />
          </Field>
          <Field label="Event Dates" htmlFor="quote-dates">
            <Input
              id="quote-dates"
              value={form.event_dates}
              onChange={set("event_dates")}
              placeholder="Nov 4–7, 2026"
              className={inputCls}
              data-testid="quote-input-dates"
            />
          </Field>
          <Field label="Selected Tier" htmlFor="quote-tier" full>
            <Select value={tier} onValueChange={onTierChange}>
              <SelectTrigger
                id="quote-tier"
                className="bg-ink/60 border-line text-slate-100 h-11 rounded-none focus:ring-electric"
                data-testid="quote-select-tier"
              >
                <SelectValue placeholder="Choose a simulator tier" />
              </SelectTrigger>
              <SelectContent className="bg-panel border-line text-slate-100 rounded-none">
                {TIER_OPTIONS.map((t) => (
                  <SelectItem
                    key={t.value}
                    value={t.value}
                    className="focus:bg-electric/10 focus:text-electric"
                    data-testid={`quote-tier-option-${t.value}`}
                  >
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Additional Details" htmlFor="quote-details" full>
            <Textarea
              id="quote-details"
              value={form.details}
              onChange={set("details")}
              placeholder="Booth size, expected foot traffic, branding needs, number of rigs..."
              className="bg-ink/60 border-line text-slate-100 placeholder:text-slate-600 focus-visible:ring-electric rounded-none min-h-[100px]"
              data-testid="quote-input-details"
            />
          </Field>
          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="quote-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-electric text-ink font-display font-bold text-sm uppercase tracking-[0.15em] px-6 py-4 clip-corner hover:bg-electric-hover disabled:opacity-60 transition-colors duration-200"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />}
              {sending ? "Submitting..." : "Submit Quote Request"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
